import { defineStore, storeToRefs } from "pinia";
import type {
  SkipReason,
  TaskAutoFixStatusResponse,
  TaskCompletionMethod,
  TaskMilestoneEnum,
  TaskStatusResponse,
} from "shared-core/types/task";
import { computed } from "vue";
import type { TaskStatusSyncOptions } from "../types/device-task-automation-service";
import { useMainStore as useSharedMainStore } from "./main-store";

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type -- returns Pinia's deeply-generic setup-store StoreDefinition, which is intractable to annotate by hand
export function createAppMainStore(
  storeId: string,
  getAuthStore: () => { user: { email: string } | null },
) {
  return defineStore(storeId, () => {
    const sharedStore = useSharedMainStore();
    const authStore = getAuthStore();

    const userTasks = computed(() => {
      const email = authStore.user?.email;
      if (!email) return [];
      return sharedStore.tasks.filter((a) => a.user === email);
    });

    const {
      tasks,
      cyberScoreStatus,
      users,
      organizationOverview,
      organizationsTasks,
      threatsIdentified,
      mfaStatus,
      milestones,
    } = storeToRefs(sharedStore);

    return {
      tasks,
      cyberScoreStatus,
      users,
      organizationOverview,
      organizationsTasks,
      threatsIdentified,
      mfaStatus,
      milestones,

      fetchTasks: (options?: TaskStatusSyncOptions): Promise<void> =>
        sharedStore.fetchTasks(options),
      fetchCyberScore: (): Promise<void> => sharedStore.fetchCyberScore(),
      fetchMfaStatus: (): Promise<void> => sharedStore.fetchMfaStatus(),
      fetchUsers: (options?: { includeDeactivated?: boolean }): Promise<void> =>
        sharedStore.fetchUsers(options),
      markAsComplete: (
        taskId: number,
        userEmail: string,
        deviceInventoryId?: string | null,
        completionMethod?: TaskCompletionMethod,
      ): Promise<TaskMilestoneEnum[]> =>
        sharedStore.markAsComplete(
          taskId,
          userEmail,
          deviceInventoryId,
          completionMethod,
        ),
      markAsSkipped: (
        taskId: number,
        userEmail: string,
        skipReason: SkipReason,
        skipReasonText?: string | null,
        skipDelayHours?: number | null,
        deviceInventoryId?: string | null,
      ): Promise<TaskMilestoneEnum[]> =>
        sharedStore.markAsSkipped(
          taskId,
          userEmail,
          skipReason,
          skipReasonText,
          skipDelayHours,
          deviceInventoryId,
        ),
      markCompletionsSeen: (taskIds: number[]): Promise<void> =>
        sharedStore.markCompletionsSeen(taskIds),
      fetchTaskAutoFixStatus: (
        taskId: number,
      ): Promise<TaskAutoFixStatusResponse> =>
        sharedStore.fetchTaskAutoFixStatus(taskId),
      refreshTaskStatus: (taskId: number): Promise<TaskStatusResponse> =>
        sharedStore.refreshTaskStatus(taskId),
      automate: (
        taskId: number,
        taskReference: string,
      ): Promise<TaskStatusResponse> =>
        sharedStore.automate(taskId, taskReference),
      fetchOrganizationOverview: (forceUpdate?: boolean): Promise<void> =>
        sharedStore.fetchOrganizationOverview(forceUpdate),
      startRiskAnalysis: (): Promise<void> => sharedStore.startRiskAnalysis(),

      userTasks,
    };
  });
}
